import React, { useEffect, useState } from 'react';
import { apiFetch } from '@/api';
import { Loader2, BookOpen, Lightbulb } from 'lucide-react';

interface NodeField {
    name: string;
    type: string;
    required: boolean;
    description: string;
}

interface NodeDoc {
    type: string;
    label: string;
    category: string;
    description: string;
    fields: NodeField[];
    tips: string[];
    modes?: any[];
    inputs?: any[];
    outputs?: any[];
}

interface NodeInfoPanelProps {
    nodeType: string;
}

const CATEGORY_COLORS: Record<string, string> = {
    Input: 'bg-blue-100 text-blue-700',
    Output: 'bg-teal-100 text-teal-700',
    Trigger: 'bg-slate-100 text-slate-700',
    Transformation: 'bg-purple-100 text-purple-700',
    AI: 'bg-indigo-100 text-indigo-700',
    Database: 'bg-orange-100 text-orange-700',
    Workflow: 'bg-green-100 text-green-700',
    ML: 'bg-amber-100 text-amber-700',
};

export function NodeInfoPanel({ nodeType }: NodeInfoPanelProps) {
    const [doc, setDoc] = useState<NodeDoc | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!nodeType) return;
        setLoading(true);
        setError(null);
        setDoc(null);

        apiFetch(`/flows/node-docs/${nodeType.toUpperCase()}`, null, { method: 'GET' })
            .then((data: any) => {
                if (data && data.type) {
                    setDoc(data as NodeDoc);
                } else {
                    setError('No documentation available for this node yet.');
                }
            })
            .catch(() => setError('No documentation available for this node yet.'))
            .finally(() => setLoading(false));
    }, [nodeType]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-xs">Loading…</span>
            </div>
        );
    }

    if (error || !doc) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400 gap-2 px-4 text-center">
                <BookOpen className="h-7 w-7 opacity-30" />
                <p className="text-xs text-gray-400">{error || 'No documentation found.'}</p>
            </div>
        );
    }

    const categoryColor = CATEGORY_COLORS[doc.category] || 'bg-gray-100 text-gray-600';

    return (
        <div className="space-y-4">
            {/* Header: badge + description */}
            <div className="space-y-2">
                <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider ${categoryColor}`}>
                    {doc.category}
                </span>
                <p className="text-xs text-gray-600 leading-relaxed">{doc.description}</p>
            </div>

            {/* Fields — flat list, no table */}
            {doc.fields && doc.fields.length > 0 && (
                <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Fields</p>
                    <div className="space-y-2">
                        {doc.fields.map((f) => (
                            <div key={f.name} className="py-1.5 border-b border-gray-50 last:border-0">
                                <div className="flex items-center gap-1 mb-0.5">
                                    <code className="text-[11px] font-mono text-pink-600">{f.name}</code>
                                    {f.required && (
                                        <span className="text-[9px] text-red-400">*</span>
                                    )}
                                </div>
                                <p className="text-[11px] text-gray-500 leading-relaxed">{f.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}


            {/* Tips */}
            {doc.tips && doc.tips.length > 0 && (
                <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                        <Lightbulb className="h-3 w-3 text-amber-400" /> Tips
                    </p>
                    <ul className="space-y-1.5">
                        {doc.tips.map((tip, i) => (
                            <li key={i} className="flex gap-2 text-[11px] text-gray-500 leading-relaxed">
                                <span className="text-amber-400 shrink-0 mt-0.5">•</span>
                                {tip}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default NodeInfoPanel;
